import React from 'react';
import StatusCard, { CSS, BrandLogo, IconUnplug } from './StatusCard';
import { CLOSE_KIND, disconnectCardCopy, gaveUpDetail } from '../utils/streamerLoss';

// A dropped stream used to render as one line of capitalised white text —
// "Disconnected code:1006. Click To Restart" — with the words themselves as the
// only thing to click. It now uses the same card as the password, sign-on and
// unavailable states, with a real button. The close code still matters for
// support, so it moves to the small detail line rather than leading.

// App.js composes the message as `Disconnected code:<n>.` plus the close reason
// when it has one. Split rather than reformatted upstream, so the code survives
// for support while the viewer reads plain words. The older wording ended in
// "Click To Restart"; that is still stripped in case a cached bundle sends it.
const parseDisconnect = (raw) => {
  const text = String(raw || '').trim();
  const code = (text.match(/code:\s*(\d+)/i) || [])[1] || '';
  const reason = text
    .replace(/disconnected\s*code:\s*\d+\.?/i, '')
    .replace(/click to restart\.?/i, '')
    .trim();
  return { code, reason };
};

// `kind` (utils/streamerLoss CLOSE_KIND) picks the card:
//   'closed'  — the viewer asked for it (terminateSession, or a page leave
//               the page came back from via the bfcache). Quiet "Stream
//               closed", no close code, one
//               "Start again" button. The ending is not a failure and must not
//               read like one.
//   'lost'    — the host or the app went away (4006 "Streamer Disconnected").
//   'gave-up' — the stream never came (the player's own wait ran out, or 4005).
//   'refused' — signalling refused admission (4401). No button at all: nothing
//               retries a ticket the server will not take.
//   undefined — every other close keeps the generic card and wording.
// `onRetry`: App.js passes a handler that starts a fresh session in place —
// new ticket, new player, no page reload, so the remembered password and the
// embedding page's state survive. Without it the button reloads the page, as it
// always did.
// `detail`: an explicit detail line (why the player gave up) when there is no
// close code to show.
// The line under the title is whatever App.js composed when it has one (a
// closed card may say the page was left, any card may say its Retry / "Start
// again" could not get a session yet); the kind's default line is the fallback.
const DisconnectScreen = ({ disconnectMessage, branding = {}, onRetry, kind, detail: detailOverride }) => {
  const { code, reason } = parseDisconnect(disconnectMessage);
  const copy = disconnectCardCopy(kind);
  const subtitle = reason || copy.subtitle;
  let detail;
  if (copy.showCode) {
    detail = kind === CLOSE_KIND.GAVE_UP
      ? (detailOverride || gaveUpDetail(undefined, code))
      : (code ? `Close code ${code}` : undefined);
  }
  // `copy.action === null`: a terminal card with no button (refused admission).
  const action = copy.action === null ? null : (onRetry ? copy.action : 'Reconnect');

  return (
    <div
      style={{
        position: 'fixed', inset: 0, width: '100vw', height: '100vh',
        backgroundColor: '#111315', zIndex: 1000000, overflow: 'hidden',
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <BrandLogo branding={branding} />
      <StatusCard
        role={kind === CLOSE_KIND.CLOSED ? 'status' : 'alert'}
        icon={<IconUnplug />}
        title={copy.title}
        subtitle={subtitle}
        detail={detail}
      >
        {action && <div className="spg-form">
          <button
            type="button"
            className="spg-btn"
            autoFocus
            onClick={onRetry || (() => window.location.reload())}
          >
            {action}
          </button>
        </div>}
      </StatusCard>
    </div>
  );
};

export default DisconnectScreen;
